import { useEnvironment } from '@wix/yoshi-flow-editor';
import React from 'react';
import {
  Avatar,
  AvatarSize,
  Button,
  ButtonSize,
  HoverStyles,
  PaddingModes,
  Text,
  ThumbnailImage,
} from 'wix-ui-tpa/cssVars';
import { DiscountName } from '../../DiscountName/DiscountName';
import { ReadMoreLink } from '../../ReadMoreLink/ReadMoreLink';
import { CourseBadge } from '../../CourseBadge/CourseBadge';
import { CourseDetails } from '../../CourseDetails/CourseDetails';
import {
  AgendaItemLayoutProps,
  STAFF_LOCATION_SEPARATOR_DATA_HOOK,
} from '../const';
import { useShowsStaffAndLocation } from '../useShowsStaffAndLocation';
import { classes, st } from './AgendaItemList.st.css';

export const AgendaItemListLayout: React.FC<AgendaItemLayoutProps> = ({
  agendaItemViewModel,
  onSlotClick,
  onReadMoreClick,
}) => {
  const {
    serviceName,
    serviceImage,
    startTime,
    duration,
    staffName,
    staffImage,
    location,
    spots,
    price,
    priceBeforeDiscount,
    priceAfterDiscount,
    discountName,
    showCalculatedAtCheckout,
    buttonText,
    disabled,
    slotId,
    readMoreText,
    readMoreAriaLabel,
    course,
  } = agendaItemViewModel || {};
  const { isMobile } = useEnvironment();
  const showsStaffAndLocation = useShowsStaffAndLocation({
    staffName,
    location,
  });

  return (
    <div
      data-hook="agenda-item-list-layout"
      className={st(classes.root, {
        isMobile,
      })}
    >
      <ThumbnailImage {...serviceImage} className={classes.serviceImage} />

      <div className={st(classes.content)}>
        <div className={st(classes.topWrapper)}>
          <div className={st(classes.nameAndDurationWrapper)}>
            <div className={classes.serviceNameRow}>
              <Text className={classes.serviceName}>{serviceName}</Text>
              <CourseBadge badgeText={course?.badgeText} />
            </div>
            <Text className={classes.startTime}>
              {startTime}
              <Text className={classes.duration}>({duration})</Text>
            </Text>
            <ReadMoreLink
              slotId={slotId}
              readMoreText={readMoreText}
              readMoreAriaLabel={readMoreAriaLabel}
              onReadMoreClick={onReadMoreClick}
            />
          </div>
          <div className={st(classes.staffAndLocationWrapper)}>
            <CourseDetails course={course} />
            {/*
              Its own box so a course can put the two names side by side
              without dragging the course lines onto that row as well.
              `CourseDetails` renders nothing for a class, so a class row is
              still one column with the same gap it has today.
            */}
            <div
              className={st(classes.staffAndLocation, { isCourse: !!course })}
            >
              <div className={classes.staff}>
                <Avatar
                  className={classes.staffImage}
                  size={AvatarSize.small}
                  src={staffImage}
                  name={staffName}
                />
                <Text className={classes.staffName}>{`${staffName}`}</Text>
              </div>
              {!!course && showsStaffAndLocation && (
                <Text
                  className={classes.staffLocationSeparator}
                  data-hook={STAFF_LOCATION_SEPARATOR_DATA_HOOK}
                  aria-hidden="true"
                >
                  •
                </Text>
              )}
              <Text className={classes.location}>{location}</Text>
            </div>
          </div>
        </div>
        <div className={st(classes.bottomWrapper)}>
          <div className={st(classes.priceAndSpotsWrapper)}>
            <div className={classes.priceContainer}>
              {priceBeforeDiscount && priceAfterDiscount ? (
                <div className={classes.pricesRow}>
                  <Text
                    className={classes.priceBeforeDiscount}
                    data-hook="agenda-item-price-before-discount"
                  >
                    {priceBeforeDiscount}
                  </Text>
                  <Text className={classes.price}>{priceAfterDiscount}</Text>
                </div>
              ) : (
                <Text className={classes.price}>{price}</Text>
              )}
              <DiscountName
                discountName={discountName}
                showCalculatedAtCheckout={showCalculatedAtCheckout}
              />
            </div>
            <Text className={classes.spots}>{spots}</Text>
          </div>
          {buttonText && (
            <div className={st(classes.buttonWrapper)}>
              <Button
                data-hook="agenda-item-cta-button"
                onClick={() => onSlotClick(slotId)}
                disabled={disabled}
                className={classes.button}
                size={ButtonSize.small}
                paddingMode={PaddingModes.explicitPaddings}
                hoverStyle={HoverStyles.underline}
              >
                {buttonText}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
